import React from 'react';
import { Box, Typography, Grid, Container } from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PaymentIcon from '@mui/icons-material/Payment';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const metrics = [
  {
    icon: GroupsIcon,
    value: '50K+',
    label: 'Students Managed',
    sub: 'Across 200+ schools',
    color: '#2563eb',
    bg: '#eff6ff',
    border: '#bfdbfe',
  },
  {
    icon: CalendarTodayIcon,
    value: '99.9%',
    label: 'Attendance Accuracy',
    sub: 'Zero manual errors',
    color: '#0891b2',
    bg: '#e0f2fe',
    border: '#bae6fd',
  },
  {
    icon: PaymentIcon,
    value: '40%',
    label: 'Fee Collection Faster',
    sub: 'Compared to manual process',
    color: '#7c3aed',
    bg: '#ede9fe',
    border: '#ddd6fe',
  },
  {
    icon: AssessmentIcon,
    value: '200+',
    label: 'Schools Onboarded',
    sub: 'Across India',
    color: '#059669',
    bg: '#d1fae5',
    border: '#a7f3d0',
  },
];

const KeyMetrics = () => (
  <Box sx={{
    py: { xs: 7, md: 9 },
    bgcolor: '#f8fafc',
    position: 'relative',
    overflow: 'hidden',
  }}>
    {/* Subtle dot grid */}
    <Box sx={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(37,99,235,0.07) 1px, transparent 1px)', backgroundSize: '24px 24px', pointerEvents: 'none' }} />

    <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
      {/* Section label */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography sx={{ color: '#2563eb', fontWeight: 700, fontSize: 13, letterSpacing: 2, fontFamily: "'Montserrat', sans-serif", textTransform: 'uppercase' }}>
          By The Numbers
        </Typography>
        <Typography variant="h3" sx={{ fontWeight: 900, color: '#0f172a', fontFamily: "'Montserrat', sans-serif", fontSize: { xs: '1.8rem', md: '2.4rem' }, mt: 1 }}>
          Trusted by Schools Across India
        </Typography>
      </Box>

      <Grid container spacing={3} justifyContent="center">
        {metrics.map((m, i) => {
          const IconComp = m.icon;
          return (
            <Grid item xs={6} md={3} key={i} sx={{ display: 'flex' }}>
              <Box sx={{
                flex: 1,
                bgcolor: '#ffffff',
                borderRadius: '24px',
                p: { xs: 3, md: 4 },
                textAlign: 'center',
                border: `1.5px solid ${m.border}`,
                boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                transition: 'all 0.3s',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: `0 20px 50px ${m.color}22`,
                  borderColor: m.color,
                },
              }}>
                {/* Top accent bar */}
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, bgcolor: m.color, borderRadius: '24px 24px 0 0' }} />

                {/* Decorative background circle */}
                <Box sx={{ position: 'absolute', width: 100, height: 100, borderRadius: '50%', bgcolor: m.bg, bottom: -30, right: -30, opacity: 0.7 }} />

                {/* Icon circle */}
                <Box sx={{
                  width: 64, height: 64,
                  borderRadius: '18px',
                  bgcolor: m.bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  mx: 'auto', mb: 2.5,
                  border: `1px solid ${m.border}`,
                  position: 'relative', zIndex: 1,
                }}>
                  <IconComp sx={{ fontSize: 30, color: m.color }} />
                </Box>

                {/* Value */}
                <Typography sx={{
                  color: m.color,
                  fontWeight: 900,
                  fontSize: { xs: '2rem', md: '2.6rem' },
                  fontFamily: "'Montserrat', sans-serif",
                  lineHeight: 1,
                  mb: 0.8,
                  position: 'relative', zIndex: 1,
                }}>
                  {m.value}
                </Typography>

                {/* Label */}
                <Typography sx={{
                  color: '#0f172a',
                  fontWeight: 800,
                  fontSize: { xs: 13, md: 14.5 },
                  fontFamily: "'Montserrat', sans-serif",
                  mb: 0.5,
                  position: 'relative', zIndex: 1,
                }}>
                  {m.label}
                </Typography>

                {/* Sub-label */}
                <Typography sx={{
                  color: '#94a3b8',
                  fontSize: 12,
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 500,
                  position: 'relative', zIndex: 1,
                }}>
                  {m.sub}
                </Typography>

                {/* Trending icon badge */}
                <Box sx={{
                  position: 'absolute', top: 16, right: 16,
                  width: 24, height: 24,
                  borderRadius: '8px',
                  bgcolor: m.bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <TrendingUpIcon sx={{ fontSize: 14, color: m.color }} />
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Container>
  </Box>
);

export default KeyMetrics;
